<script setup lang="ts">
import type { QuizQuestionData } from '@vue-quiz-app/types/quiz';
import { ref, nextTick } from 'vue';

const props = defineProps<{
  questionData: QuizQuestionData;
  currentQuestion: number;
  totalQuestions: number;
  isWeighted: boolean;
}>();

const emit = defineEmits<{
  'next-question': [];
  'add-points': [id: string, p: number];
}>();

const selectedChoice = ref('');

async function selectVariant(ch: string) {
  selectedChoice.value = ch;
  if (props.isWeighted) {
    emit('add-points', props.questionData.id, props.questionData.points[ch]);
  } else {
    emit('add-points', props.questionData.id, props.questionData.correct === ch ? 1 : 0);
  }
  await nextTick();
  document.querySelector('.quiz-next')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  checkLazyLoadedImages();
}

function nextQuestion() {
  emit('next-question');
  document.querySelector('#quiz-app')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  selectedChoice.value = '';
}

function checkLazyLoadedImages() {
  // Remove lazy load from images
  document.querySelectorAll<HTMLImageElement>('#quiz-app .quiz-more img.b-lazy').forEach((el) => {
    if (el.dataset.src) el.src = el.dataset.src;
  });
}
</script>

<template>
  <div>
    <img
      v-if="props.questionData.image"
      :src="props.questionData.image"
      alt=""
    />
    <div class="quiz-question-section">
      <div class="current-question-marker">
        {{ props.currentQuestion }}
      </div>
      <div class="current-question-of-all">{{ props.currentQuestion }} of {{ props.totalQuestions }}</div>

      <h3 v-html="props.questionData.question"></h3>
      <p
        v-if="props.questionData.description"
        v-html="props.questionData.description"
      ></p>

      <div>
        <button
          v-for="(ch, index) in props.questionData.choices"
          :key="index"
          :id="`quiz-choices-${index}`"
          :class="{
            correct:
              selectedChoice &&
              ((props.isWeighted && props.questionData.points[index] && selectedChoice === index) ||
                (!props.isWeighted && props.questionData.correct === index)),
            incorrect:
              selectedChoice &&
              ((props.isWeighted && props.questionData.points[index] === 0 && selectedChoice === index) ||
                (!props.isWeighted && selectedChoice === index && props.questionData.correct !== index)),
          }"
          name="quiz-choices"
          type="button"
          :disabled="!!selectedChoice"
          @click="selectVariant(index)"
        >
          <span v-html="ch"></span>
          <span
            v-if="props.isWeighted && selectedChoice"
            class="quiz-question-choice-points"
            :class="{ incorrect: props.questionData.points[index] === 0 }"
            >({{ props.questionData.points[index] }} point{{ props.questionData.points[index] === 1 ? '' : 's' }})</span
          >
        </button>
      </div>
    </div>
    <template v-if="selectedChoice">
      <div class="quiz-next">
        <button
          class="btn red quiz-button"
          :class="{ 'mb-4': !props.questionData.moreInfo && !props.questionData.learnMore }"
          @click="nextQuestion"
        >
          Next Question
        </button>
      </div>
      <div
        v-if="props.questionData.moreInfo || props.questionData.learnMore"
        class="quiz-more"
      >
        <h4>{{ props.isWeighted ? 'Here’s Why' : 'Here’s More' }}</h4>
        <p
          v-if="props.questionData.moreInfo"
          v-html="props.questionData.moreInfo"
        ></p>
        <a
          v-if="props.questionData.learnMore"
          :href="props.questionData.learnMore"
          target="_blank"
          class="link-cta"
          >Learn More</a
        >
      </div>
    </template>
  </div>
</template>

<style></style>
